import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import { TOKEN_KEY } from '../config/api';

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
export let globalSocket: Socket | null = null;

/** Tear down the shared socket on logout so the next session does not reuse a stale token. */
export function disconnectGlobalSocket() {
  if (!globalSocket) return;
  try {
    globalSocket.removeAllListeners();
    globalSocket.disconnect();
  } catch {
    // ignore
  }
  globalSocket = null;
  window.dispatchEvent(new Event('meraayurved:socket-disconnected'));
}

export const useSocket = () => {
  const { user } = useAuth();
  // Prefer the live global instance so late-mounted pages (e.g. My Team) still
  // get a usable socket even if their local state hasn't synced yet.
  const [activeSocket, setActiveSocket] = useState<Socket | null>(globalSocket);
  const initialized = useRef(false);
  /** Which user id the current globalSocket was opened for (impersonation must reconnect). */
  const socketUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const userId = user?.id != null ? String(user.id) : null;
    if (!userId) {
      // Logged out — drop any leftover connection (token already cleared).
      if (globalSocket) {
        disconnectGlobalSocket();
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setActiveSocket(null);
      }
      socketUserIdRef.current = null;
      initialized.current = false;
      return;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    // Same user + live socket → reuse. Different user (impersonation) → tear down.
    if (
      globalSocket &&
      globalSocket.connected &&
      socketUserIdRef.current === userId
    ) {
      setActiveSocket(globalSocket);
      initialized.current = true;
      return;
    }

    if (globalSocket) {
      disconnectGlobalSocket();
    }

    console.log(
      '👀 [useSocket] Creating new socket connection to:',
      SOCKET_URL,
    );
    globalSocket = io(SOCKET_URL, {
      auth: (cb) => {
        cb({ token: localStorage.getItem(TOKEN_KEY) });
      },
      transports: ['websocket', 'polling'],
      timeout: 8000,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 3,
    });
    socketUserIdRef.current = userId;

    globalSocket.on('connect', () => {
      console.log('🔌 WebSocket connected');
      setActiveSocket(globalSocket);
      // Wake other hook instances that mounted before connect.
      window.dispatchEvent(new Event('meraayurved:socket-ready'));
    });

    globalSocket.on('disconnect', (reason) => {
      console.log('🔌 WebSocket disconnected:', reason);
    });

    globalSocket.on('connect_error', (err) => {
      // Timeout is common when the API process has no Socket.IO (or is overloaded).
      // It does not block page data loading — keep the console quiet.
      const msg = err?.message || String(err);
      if (/timeout|xhr poll error|websocket error/i.test(msg)) {
        console.warn(
          'WebSocket unavailable — realtime notifications disabled:',
          msg,
        );
      } else {
        console.error('WebSocket connection error:', msg);
      }
    });

    initialized.current = true;
    setActiveSocket(globalSocket);
  }, [user?.id]);

  useEffect(() => {
    const onReady = () => {
      if (globalSocket) setActiveSocket(globalSocket);
    };
    window.addEventListener('meraayurved:socket-ready', onReady);
    return () => {
      window.removeEventListener('meraayurved:socket-ready', onReady);
    };
  }, []);

  return activeSocket ?? globalSocket;
};

// Mounts at App level to keep the socket alive across page navigations
// and forward notification kinds into window events so any open page can refetch.
export const SocketProvider = () => {
  const socket = useSocket();

  useEffect(() => {
    if (!socket) return;

    const onAppNotification = (payload: {
      kind?: string;
      message?: string;
      meta?: {
        reviewId?: string | number;
        status?: string;
        reviewedBy?: string | number;
      };
      data?: {
        recipeId?: string | number;
        recipeUuid?: string;
        status?: string;
        action?: string;
        visibility_scope?: string;
        title?: string;
        teamId?: number;
        actorUserId?: number;
        actorName?: string;
      };
    }) => {
      const kind = payload?.kind;
      if (!kind) return;

      if (kind === 'team_invites') {
        window.dispatchEvent(new Event('meraayurved:team-invites-changed'));
        return;
      }

      if (kind === 'pending_doctors') {
        window.dispatchEvent(new Event('meraayurved:pending-doctors-changed'));
        return;
      }

      if (
        kind === 'review_comment_pending' ||
        kind === 'review_comment_decided'
      ) {
        window.dispatchEvent(
          new CustomEvent('meraayurved:comment-moderation', {
            detail: {
              kind,
              reviewId: payload?.meta?.reviewId,
              status: payload?.meta?.status,
              reviewedBy: payload?.meta?.reviewedBy,
            },
          }),
        );
        return;
      }

      if (kind === 'doctor_review_queue') {
        window.dispatchEvent(
          new CustomEvent('meraayurved:doctor-review-queue-changed', {
            detail: {
              recipeId: payload?.data?.recipeId,
              status: payload?.data?.status,
            },
          }),
        );
        return;
      }

      if (
        kind === 'family_invites' ||
        kind === 'family_share_invites' ||
        kind === 'family_ownership'
      ) {
        window.dispatchEvent(
          new CustomEvent('meraayurved:family-changed', {
            detail: { kind, data: payload?.data },
          }),
        );
        return;
      }

      if (kind === 'recipe_status') {
        window.dispatchEvent(
          new CustomEvent('meraayurved:my-recipes-changed', {
            detail: {
              recipeId: payload?.data?.recipeId,
              status: payload?.data?.status,
              action: payload?.data?.action,
            },
          }),
        );
        window.dispatchEvent(
          new CustomEvent('meraayurved:recipe-status-changed', {
            detail: {
              recipeId: payload?.data?.recipeId,
              status: payload?.data?.status,
              action: payload?.data?.action,
            },
          }),
        );
        return;
      }

      if (kind === 'recipe_team_access') {
        window.dispatchEvent(
          new CustomEvent('meraayurved:recipe-team-access', {
            detail: { ...(payload?.data || {}), message: payload?.message },
          }),
        );
        window.dispatchEvent(
          new CustomEvent('meraayurved:my-recipes-changed', {
            detail: {
              recipeId: payload?.data?.recipeId,
              recipeUuid: payload?.data?.recipeUuid,
              action: payload?.data?.action,
              visibility_scope: payload?.data?.visibility_scope,
              status: payload?.data?.status,
              actorUserId: payload?.data?.actorUserId,
              message: payload?.message,
            },
          }),
        );
        return;
      }

      if (kind === 'recipe_queue') {
        window.dispatchEvent(
          new CustomEvent('meraayurved:recipe-status-changed', {
            detail: {
              recipeId: payload?.data?.recipeId,
              status: payload?.data?.status,
            },
          }),
        );
      }
    };

    socket.on('app_notification', onAppNotification);
    return () => {
      socket.off('app_notification', onAppNotification);
    };
  }, [socket]);

  return null;
};
